export type OlympicRegion = "SUDO" | "GYEONGSANG" | "CHUNGJEON";

export type OlympicTeamType = "SPARKS" | "TNT_F" | "TNT_M" | "TNT_MIXED";

export type Gender = "M" | "F";

export interface OlympicRegionMeta {
  code: OlympicRegion;
  label: string;
  date: string;
  place: string;
}

export const OLYMPIC_REGIONS: OlympicRegionMeta[] = [
  { code: "SUDO",       label: "수도권 지역",   date: "5월 23일", place: "인천 남동체육관" },
  { code: "GYEONGSANG", label: "경상지역",     date: "5월 23일", place: "울산문수체육관" },
  { code: "CHUNGJEON",  label: "충청 전라지역", date: "5월 30일", place: "무주 태권도원" },
];

export const OLYMPIC_TEAM_META: Record<OlympicTeamType, { label: string; allowMultiple: boolean; playerGenderEditable: boolean; }> = {
  SPARKS:    { label: "스팍스",    allowMultiple: true,  playerGenderEditable: false },
  TNT_F:     { label: "T&T 여자", allowMultiple: true,  playerGenderEditable: false },
  TNT_M:     { label: "T&T 남자", allowMultiple: true,  playerGenderEditable: false },
  TNT_MIXED: { label: "T&T 혼성", allowMultiple: false, playerGenderEditable: true  },
};

export interface OlympicContact {
  name: string;
  contact: string;
}

export interface OlympicCoach {
  name: string;
  contact: string;
  gender?: Gender;
}

export interface OlympicPlayer {
  index: number;        // 1..10 정규, 11/12 교체
  name: string;
  gender?: Gender;      // 혼성팀: 1~5 "F" 고정, 6~10 선택
  grade?: string;       // 예: "초3", "중1"
  isSubstitute?: boolean;
}

export interface OlympicTeam {
  id: string;           // uuid (client-side)
  teamType: OlympicTeamType;
  teamLabel: string;    // "A팀", "B팀", ...
  coaches: OlympicCoach[];   // 길이 2 고정
  players: OlympicPlayer[];  // 선수 10 + 교체 2 = 12
}

export interface OlympicBasicInfo {
  region: OlympicRegion | "";
  church_number: string;
  church_name: string;
  receptionManager: OlympicContact;
  sparksSupervisor: OlympicContact;
  tntSupervisor: OlympicContact;
  pastor: OlympicContact;
}

export interface OlympicMealCount {
  elem: number;
  midHigh: number;
  general: number;
}

export interface OlympicCostBreakdown {
  teamFee: number;
  observers: number;
  meals: number;
  canopies: number;
  total: number;
}

export interface OlympicRegistration {
  basicInfo: OlympicBasicInfo;
  teams: OlympicTeam[];
  observerCount: number;
  mealCount: OlympicMealCount;
  canopyCount: number;
  costs: OlympicCostBreakdown;
}

export interface OlympicRegistrationData extends OlympicRegistration {
  _id?: string;
  eventId?: string;
  churchId?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED";
  createdAt?: string;
  updatedAt?: string;
}

export const OLYMPIC_PRICE = {
  TEAM_FEE: 350_000,
  OBSERVER: 5_000,
  MEAL_ELEM: 6_000,
  MEAL_MID_HIGH: 7_500,
  MEAL_GENERAL: 8_000,
  CANOPY: 50_000,
} as const;

export function canopyMaxCount(totalTeamCount: number): number {
  if (totalTeamCount < 1) return 0;
  if (totalTeamCount <= 3) return 1;
  return 2;
}

export function isTeamQualified(team: OlympicTeam): boolean {
  const regulars = team.players.filter((p) => !p.isSubstitute);
  if (regulars.length < 10) return false;
  return regulars.slice(0, 10).every((p) => (p.name || "").trim().length > 0);
}

export function countQualifiedTeams(teams: OlympicTeam[]): number {
  return teams.filter(isTeamQualified).length;
}

export const SPARKS_GRADE_OPTIONS = ["7세", "1학년", "2학년"] as const;
export const TNT_GRADE_OPTIONS = ["1학년", "2학년", "3학년", "4학년", "5학년", "6학년"] as const;

export function gradeOptionsFor(teamType: OlympicTeamType): readonly string[] {
  return teamType === "SPARKS" ? SPARKS_GRADE_OPTIONS : TNT_GRADE_OPTIONS;
}

export function calculateOlympicCosts(reg: Pick<OlympicRegistration, "basicInfo" | "teams" | "observerCount" | "mealCount" | "canopyCount">): OlympicCostBreakdown {
  const { basicInfo, teams, observerCount, mealCount, canopyCount } = reg;
  const teamFee = OLYMPIC_PRICE.TEAM_FEE * countQualifiedTeams(teams);
  const observers = OLYMPIC_PRICE.OBSERVER * (observerCount || 0);

  const meals =
    basicInfo.region === "CHUNGJEON"
      ? OLYMPIC_PRICE.MEAL_ELEM * (mealCount.elem || 0) +
        OLYMPIC_PRICE.MEAL_MID_HIGH * (mealCount.midHigh || 0) +
        OLYMPIC_PRICE.MEAL_GENERAL * (mealCount.general || 0)
      : 0;

  const canopies =
    basicInfo.region === "SUDO" || basicInfo.region === "GYEONGSANG"
      ? OLYMPIC_PRICE.CANOPY * (canopyCount || 0)
      : 0;

  return {
    teamFee,
    observers,
    meals,
    canopies,
    total: teamFee + observers + meals + canopies,
  };
}
